import dayjs from 'utils/dayjs'

import { formatDurationLong } from 'components_new/organisms/internal/DataRuns/utils'
import {
  DataRerunCustomer,
  DataRerunSummary
} from 'redux/selectors/internal/DataReruns'

type TagColor = 'default' | 'error' | 'info' | 'success' | 'warning'

interface StatusDisplay {
  color: TagColor
  label: string
}

/** Label and Tag colour of one customer in a rerun. */
export function customerStatusDisplay(
  customer: DataRerunCustomer
): StatusDisplay {
  switch (customer.status) {
  case 'queued':
    return { color: 'default', label: 'Köad' }
  case 'running':
    return customer.step === 'collect'
      ? { color: 'info', label: 'Kör insamling' }
      : { color: 'info', label: 'Kör SIM' }
  case 'done':
    return { color: 'success', label: 'Klar' }
  case 'collect_failed':
    return { color: 'error', label: 'Fel i insamling' }
  case 'sim_failed':
    return { color: 'error', label: 'Fel i SIM' }
  case 'cancelled':
    return { color: 'default', label: 'Avbruten' }
  case 'skipped':
    return { color: 'warning', label: 'Överhoppad' }
  }
}

/**
 * Outcome of a whole rerun: Pågår until it finishes, then Avbruten, Klar med
 * fel when any customer failed, else Klar.
 */
export function rerunOutcome(rerun: DataRerunSummary): StatusDisplay {
  if (rerun.status === 'running') {
    return { color: 'info', label: 'Pågår' }
  }

  if (rerun.status === 'cancelled') {
    return { color: 'warning', label: 'Avbruten' }
  }

  return rerun.failed_count > 0
    ? { color: 'error', label: 'Klar med fel' }
    : { color: 'success', label: 'Klar' }
}

/** "2 av 8 kunder klara · 1 fel", plus överhoppade when there are any. */
export function formatRerunProgress(rerun: DataRerunSummary): string {
  const parts = [
    `${rerun.done_count} av ${rerun.customer_count} kunder klara`,
    `${rerun.failed_count} fel`
  ]

  if (rerun.skipped_count > 0) {
    parts.push(`${rerun.skipped_count} överhoppade`)
  }

  return parts.join(' · ')
}

/** Wall-clock length of a finished rerun, "–" while it runs. */
export function formatRerunDuration(rerun: DataRerunSummary): string {
  if (!rerun.finished_at) {
    return '–'
  }

  return formatDurationLong(
    dayjs(rerun.finished_at).diff(dayjs(rerun.started_at), 'minute', true)
  )
}

/** Swedish text for the driver's skipped_reason codes. */
export function formatSkippedReason(reason: string | null): string {
  if (!reason) {
    return 'Överhoppad'
  }

  if (reason === 'sim_job_missing') {
    return 'Kunden har inget SIM-jobb.'
  }

  const [code, jobName] = reason.split(':')

  if (code === 'glue_job_missing') {
    return `Glue-jobbet ${jobName} finns inte.`
  }

  return reason
}

/** Summed cost of the customer's runs, null when none has a cost. */
export function customerCostUsd(customer: DataRerunCustomer): number | null {
  const costs = customer.jobs
    .map((job) => job.cost_usd)
    .filter((cost): cost is number => cost !== null)

  return costs.length === 0 ? null : costs.reduce((sum, cost) => sum + cost, 0)
}
